<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('tenants.tenants') }}: {{ product.name }}</h1>
      <div class="details-component__header_right">
        <a v-if="!isFormOpen" class="link" v-on:click="toggleForm">
          {{ $t('tenants.add_tenant') }}
          <i class="icon icon--plus"></i>
        </a>
        <a v-else class="disabled link" v-on:click="toggleForm">{{ $t('common.close') }}
          <i class="icon icon--cross"></i>
        </a>
      </div>
    </div>

    <autocomplete class="details-component__lookupform inline-search" v-bind:class="{ active: isActive }"
      lookup="tenants" :filter="autocompleteTenantFilter" v-on:query="selectTenant" v-if="isFormOpen">
      <template slot="inner-item" slot-scope="props">
        <span>{{ props.item.name }}</span>
        <span class="disabled">{{ props.item.address }}</span>
      </template>
    </autocomplete>

    <div class="details-component__lookupform details-component__lookupform-confirm"
      v-if="isFormOpen && tenant !== null" v-bind:class="{ active: isActive }">
      <form class="form">
        <div class="form__field">
          <label class="form__label">{{ $t('common.tenant') }}</label>
          <div>{{ tenant.name }}</div>
        </div>

        <div class="form__field" :class="{ 'form__field_error': errors.has('amount') }"
          v-if="product.type === 'permitPack'">
          <label for="amount" class="form__label">{{ $t('common.amount') }}</label>
          <input type="text" id="amount" class="form__input" v-model="form.amount" v-validate
            data-vv-rules="required|numeric|max:4|cannotBeLower" name="amount" ref="amount" />
        </div>

        <div class="form__buttons buttonset">
          <button type="button" class="buttonset__button btn btn--secondary" v-on:click.prevent.stop="toggleForm()">
            {{ $t('common.discard') }}
          </button>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="addTenant()">
            {{ $t('tenants.submit') }}
          </button>
        </div>

      </form>
    </div>

    <div class="details-component__content content" v-if="product.partners">
      <v-client-table class="mobile-650" :data="product.partners" :columns="columns" :options="options">
        <span slot="name" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('common.name') }}:
          </span>
          <a class="link" @click="routeToTenant(props.row)">
            <span>{{ props.row.name }}</span>
          </a>
        </span>
        <span slot="contactPerson" slot-scope="props" class="mobile-padding"><span class="mobile">{{
        $t('tenants.contact_person') }}: </span><span class="total">{{ props.row.contactPerson }}</span></span>
        <span slot="address" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('common.address') }}:
          </span><span class="total">{{ props.row.address }}</span></span>
        <div slot="removeCompany" slot-scope="props" class="delete-wrapper">
          <a @click="showConfirmationForRemovingTenant(props.row)">
            <span class="delete-wrapper__icon icon icon--cross"></span>
          </a>
        </div>
        <div slot="issued" slot-scope="props">
          <span class="mobile">{{ $t('common.issued') }}: </span>
          <span class="total-permit-count" v-show="!isSelectedRow(props.row.id)" @click="setSelectedRow(props.row)">
            {{ props.row.issued }}
            <a class="edit-wrapper">
              {{ $t('common.change') }}
              <i class="icon icon--arrow" />
            </a>
          </span>
          <form class="form" v-show="isSelectedRow(props.row.id)">
            <input @input="e => editTotalPermitAmount('permitInput', e.target.value)" class="form__input--editing"
              :value="props.row.issued" name="permit_input" :ref="'changeIssued' + props.row.id"
              @keydown.prevent.stop.enter="submitEdit(props.row.id)" />
            <div class="form__submit-edit" @click="submitEdit(props.row.id)"></div>
          </form>
        </div>
      </v-client-table>
    </div>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelTenantRemove" @confirmed="removeCompany">
      <h1 slot="header">{{ $t('tenants.removing') }}: {{ removeTenantName }} </h1>
      <p slot="body">{{ $t('tenants.confirm_remove') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ tenantWarningHeader }}</h1>
      <p slot="body">{{ tenantWarningBody }}</p>
    </WarningModal>
  </div>

</template>

<script>
import { mapGetters } from 'vuex';
import 'styles/vue-table.scss';
import * as mTypes from 'store/mutation-types';
import Autocomplete from 'common/Autocomplete';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    Autocomplete,
    ConfirmationModal,
    WarningModal,
  },
  computed: mapGetters({
    product: 'products/product/product',
    selectedProductId: 'products/product/selectedProductId',
    productsLimit: 'landlord/products/limit',
    productsKeywordFilter: 'landlord/products/keywordFilter',
    tenantsError: 'products/tenants/error',
    user: 'user/user/user',
  }),
  route: {
    canReuse: true,
  },
  watch: {
    product: 'refreshColumns',
  },
  created() {
    this.$validator.remove('cannotBeLower');
    this.createAmountValidator();
  },
  methods: {
    toggleForm() {
      this.isFormOpen = !this.isFormOpen;
      this.isActive = !this.isActive;
      if (this.isFormOpen) {
        this.$nextTick(() => {
          this.$el.getElementsByClassName('autocomplete__input')[0].focus();
        });
      } else {
        this.tenant = null;
        this.form.amount = null;
      }
    },
    selectTenant(q) {
      this.tenant = q.item;
      this.$nextTick(() => {
        if (this.product.type === 'permitPack') {
          this.$refs.amount.focus();
        }
      });
    },
    autocompleteTenantFilter(data) {
      if (!this.isSuperAdmin()) {
        return data;
      }
      return data.filter(value => value.parent_partner_id === this.product.partner.id);
    },
    addTenant() {
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.$store.dispatch('products/tenants/add', {
            companyId: this.tenant.id,
            productId: this.product.id,
            amount: this.form.amount,
            user: this.user,
          })
            .then(() => {
              if (this.tenantsError) {
                this.displayError();
              } else {
                this.fetchProduct();
                this.fetchProductList();
                this.toggleForm();
              }
            });
        });
    },
    displayError() {
      this.tenantWarningHeader = this.$t('common.error');
      this.showWarningModal = true;
      switch (this.tenantsError) {
        case 'invalid_product_amount':
          this.tenantWarningBody = this.$t('errors.invalid_product_amount');
          break;
        case 'product_amount_lower_than_permits_count':
          this.tenantWarningBody = this.$t('errors.product_amount_lower_than_permits_count');
          break;
        case 'cannot_remove_product_with_issued_permits':
          this.tenantWarningBody = this.$t('errors.cannot_remove_tenant_with_issued_permits');
          break;
        case 'partner_product_relation_already_exists_for_pack':
          this.tenantWarningBody = this.$t('errors.partner_product_relation_already_exists_for_pack');
          break;
        default:
          this.tenantWarningBody = this.$t('errors.zone_capacity_exceeded');
      }
    },
    removeCompany() {
      this.showConfirmationModal = false;
      this.$store.dispatch('products/tenants/delete', {
        companyId: this.removeTenantId,
        productId: this.product.id,
        user: this.user,
      }).then(() => {
        if (this.tenantsError) {
          this.displayError();
        }
      });
      this.removeTenantId = null;
      this.fetchProduct();
      this.fetchProductList();
    },
    showConfirmationForRemovingTenant(tenant) {
      this.removeTenantId = tenant.id;
      this.removeTenantName = tenant.name;
      this.showConfirmationModal = true;
    },
    cancelTenantRemove() {
      this.showConfirmationModal = false;
      this.removeTenantId = null;
      this.removeTenantName = null;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`products/tenants/${mTypes.DATA_ERROR}`, false);
    },
    submitEdit(tenantId) {
      if (this.oldValue.toString() === this.$refs[`changeIssued${tenantId}`].value) {
        this.setSelectedRow(null);
        return false;
      }
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.$store.dispatch('products/tenants/edit', {
            companyId: tenantId,
            productId: this.product.id,
            amount: this.permitInput,
            user: this.user,
          })
            .then(() => {
              if (this.tenantsError) {
                this.displayError();
              } else {
                this.fetchProduct();
                this.fetchProductList();
              }
            })
            .catch(() => {
            });
        });
      this.setSelectedRow(null);
      return false;
    },
    editTotalPermitAmount(prop, value) {
      if (value >= 0) {
        this[prop] = value;
        this.permit_value = this[prop];
      }
    },
    fetchProduct() {
      this.$store.dispatch('products/product/fetch', { id: this.$route.params.id, user: this.user });
    },
    fetchProductList() {
      this.$store.dispatch('landlord/products/fetch', { user: this.user, limit: this.productsLimit, keyword: this.productsKeywordFilter });
    },
    setSelectedRow(row) {
      const rowId = row && row.id ? row.id : row;
      this.selectedRow.id = rowId;
      this.selectedRow.productId = this.product.id;
      this.$nextTick(() => {
        if (this.isSelectedRow(rowId) && rowId) {
          this.$refs[`changeIssued${rowId}`].focus();
        }
      });
      this.oldValue = row && row.issued ? row.issued : row;
    },
    isSelectedRow(rowId) {
      if (this.selectedRow.productId !== this.product.id) {
        this.setSelectedRow(null);
      }
      return this.selectedRow.id === rowId;
    },
    createAmountValidator() {
      this.$validator.extend('cannotBeLower', {
        getMessage: () => 'Number cannot be lower then assigned permits',
        validate: (value) => {
          if (this.product.type === 'permitPack' && value < 1) {
            return false;
          }
          return true;
        },
      });
    },
    refreshColumns() {
      this.columns = this.product.type === 'permitPack' ?
        ['name', 'contactPerson', 'address', 'issued', 'removeCompany', 'edit'] :
        ['name', 'contactPerson', 'address', 'removeCompany', 'edit'];
    },
    routeToTenant(tenant) {
      this.$router.push({ name: 'tenant', params: { id: tenant.id }, query: { keyword: tenant.name } });
    },
  },
  data() {
    return {
      showConfirmationModal: false,
      showWarningModal: false,
      removeTenantId: null,
      removeTenantName: null,
      isActive: false,
      permitInput: null,
      oldValue: null,
      selectedRow: {
        id: null,
        productId: null,
      },
      tenant: null,
      isFormOpen: false,
      form: {},
      columns: ['name', 'contactPerson', 'address', 'issued', 'removeCompany', 'edit'],
      options: {
        texts: {
          noResults: this.$t('tenants.no_matching_records'),
          count: this.$t('tables.count'),
        },
        filterable: false,
        rowClassCallback: () => 'VueTables__row',
        headings: {
          name: () => this.$t('common.name'),
          contactPerson: () => this.$t('tenants.contact_person'),
          address: () => this.$t('common.address'),
          issued: () => this.$t('common.issued'),
          removeCompany: () => (''),
          edit: () => (''),
        },
        perPage: 10,
      },
    };
  },
};
</script>
<style lang="scss" scoped>
@import '~styles/blocks/details.scss';
</style>



// WEBPACK FOOTER //
// src/components/products/TenantsTable.vue